import { Layout } from '../components/Layout';
import { useState, useEffect } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Plus, Search, Phone, Cake, Heart, IndianRupee, ShoppingBag } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../components/ui/dialog';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;
const auth = () => ({ headers: { Authorization: `Bearer ${localStorage.getItem('token')}` } });
const fmt = (n) => `₹${Number(n || 0).toFixed(2)}`;

const empty = { name: '', phone: '', email: '', address: '', gstin: '', birthday: '', anniversary: '', favourite_flavours: '', egg_preference: 'both', allergies: '', notes: '' };

const Field = ({ label, children }) => (
  <div><label className="block text-xs font-medium text-stone-500 mb-1">{label}</label>{children}</div>
);

export default function Customers({ user, onLogout }) {
  const [customers, setCustomers] = useState([]);
  const [search, setSearch] = useState('');
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(empty);
  const [detail, setDetail] = useState(null);

  const load = (q = '') => {
    axios.get(`${API}/customers${q ? `?search=${encodeURIComponent(q)}` : ''}`, auth()).then((r) => setCustomers(r.data)).catch(() => {});
  };
  useEffect(() => { load(); }, []);
  useEffect(() => { const t = setTimeout(() => load(search), 300); return () => clearTimeout(t); }, [search]);

  const save = async () => {
    if (!form.name || !form.phone) return toast.error('Name & phone required');
    try {
      await axios.post(`${API}/customers`, form, auth());
      toast.success('Customer saved');
      setOpen(false); setForm(empty); load(search);
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed'); }
  };

  const openDetail = async (c) => {
    const r = await axios.get(`${API}/customers/${c.customer_id}`, auth());
    setDetail(r.data);
  };

  return (
    <Layout user={user} onLogout={onLogout}>
      <div className="p-8">
        <div className="flex justify-between items-center mb-6">
          <div>
            <h1 className="font-heading text-4xl font-bold text-stone-900">Customers</h1>
            <p className="text-stone-600 mt-1">CRM & order history</p>
          </div>
          <Button data-testid="new-customer-button" onClick={() => { setForm(empty); setOpen(true); }} className="bg-secondary hover:bg-secondary-hover text-black">
            <Plus size={20} className="mr-2" /> Add Customer
          </Button>
        </div>

        <div className="relative mb-5 max-w-md">
          <Search size={18} className="absolute left-3 top-1/2 -translate-y-1/2 text-stone-400" />
          <Input data-testid="customer-search" placeholder="Search by name or phone..." value={search} onChange={(e) => setSearch(e.target.value)} className="pl-10 h-11" />
        </div>

        <div className="bg-white border border-stone-200 rounded-sm shadow-sm overflow-x-auto">
          <table className="w-full">
            <thead className="bg-stone-50">
              <tr>
                <th className="px-4 py-3 text-left text-xs font-medium text-stone-500 uppercase">Name</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-stone-500 uppercase">Phone</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-stone-500 uppercase">Preference</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-stone-500 uppercase">Birthday</th>
              </tr>
            </thead>
            <tbody>
              {customers.map((c) => (
                <tr key={c.customer_id} className="border-b border-stone-100 hover:bg-stone-50 cursor-pointer" data-testid={`customer-row-${c.customer_id}`} onClick={() => openDetail(c)}>
                  <td className="px-4 py-3 font-medium">{c.name}</td>
                  <td className="px-4 py-3 text-stone-600">{c.phone}</td>
                  <td className="px-4 py-3 capitalize text-sm">{c.egg_preference}</td>
                  <td className="px-4 py-3 text-sm text-stone-600">{c.birthday || '—'}</td>
                </tr>
              ))}
              {customers.length === 0 && <tr><td colSpan={4} className="text-center py-12 text-stone-400">No customers yet</td></tr>}
            </tbody>
          </table>
        </div>
      </div>

      {/* Add customer dialog */}
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-auto">
          <DialogHeader><DialogTitle>Add Customer</DialogTitle></DialogHeader>
          <div className="grid grid-cols-2 gap-3 py-2">
            <Field label="Name *"><Input data-testid="customer-name" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} /></Field>
            <Field label="Phone *"><Input data-testid="customer-phone" value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} /></Field>
            <Field label="Email"><Input value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} /></Field>
            <Field label="GSTIN"><Input value={form.gstin} onChange={(e) => setForm({ ...form, gstin: e.target.value })} /></Field>
            <Field label="Birthday"><Input type="date" value={form.birthday} onChange={(e) => setForm({ ...form, birthday: e.target.value })} /></Field>
            <Field label="Anniversary"><Input type="date" value={form.anniversary} onChange={(e) => setForm({ ...form, anniversary: e.target.value })} /></Field>
            <Field label="Egg Preference">
              <Select value={form.egg_preference} onValueChange={(v) => setForm({ ...form, egg_preference: v })}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent><SelectItem value="both">Both</SelectItem><SelectItem value="eggless">Eggless</SelectItem><SelectItem value="egg">Egg</SelectItem></SelectContent>
              </Select>
            </Field>
            <Field label="Favourite Flavours"><Input value={form.favourite_flavours} onChange={(e) => setForm({ ...form, favourite_flavours: e.target.value })} /></Field>
            <Field label="Allergies"><Input value={form.allergies} onChange={(e) => setForm({ ...form, allergies: e.target.value })} /></Field>
            <div className="col-span-2"><Field label="Address"><Input value={form.address} onChange={(e) => setForm({ ...form, address: e.target.value })} /></Field></div>
            <div className="col-span-2"><Field label="Notes"><Input value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} /></Field></div>
          </div>
          <Button data-testid="save-customer-button" onClick={save} className="w-full bg-primary text-white">Save Customer</Button>
        </DialogContent>
      </Dialog>

      {/* Customer detail dialog */}
      <Dialog open={!!detail} onOpenChange={(o) => !o && setDetail(null)}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-auto" data-testid="customer-detail">
          {detail && (
            <>
              <DialogHeader><DialogTitle>{detail.customer.name}</DialogTitle></DialogHeader>
              <div className="space-y-5 py-2">
                <div className="grid grid-cols-3 gap-3">
                  <div className="bg-stone-50 rounded-lg p-4 flex items-center gap-3"><ShoppingBag className="text-primary" size={20} /><div><div className="text-xs text-stone-500">Orders</div><div className="text-xl font-bold">{detail.stats.total_orders}</div></div></div>
                  <div className="bg-stone-50 rounded-lg p-4 flex items-center gap-3 col-span-2"><IndianRupee className="text-secondary" size={20} /><div><div className="text-xs text-stone-500">Lifetime Value</div><div className="text-xl font-bold">{fmt(detail.stats.lifetime_value)}</div></div></div>
                </div>
                <div className="grid grid-cols-2 gap-x-6 gap-y-2 text-sm">
                  <div className="flex items-center gap-2"><Phone size={14} className="text-stone-400" /> {detail.customer.phone}</div>
                  <div className="flex items-center gap-2"><Cake size={14} className="text-stone-400" /> {detail.customer.birthday || '—'}</div>
                  <div className="flex items-center gap-2"><Heart size={14} className="text-stone-400" /> {detail.customer.anniversary || '—'}</div>
                  <div><span className="text-stone-500">Pref:</span> {detail.customer.egg_preference}</div>
                  {detail.customer.favourite_flavours && <div className="col-span-2"><span className="text-stone-500">Flavours:</span> {detail.customer.favourite_flavours}</div>}
                  {detail.customer.allergies && <div className="col-span-2 text-functional-error"><span className="text-stone-500">Allergies:</span> {detail.customer.allergies}</div>}
                  {detail.customer.notes && <div className="col-span-2"><span className="text-stone-500">Notes:</span> {detail.customer.notes}</div>}
                </div>
                <div>
                  <div className="font-medium text-stone-800 mb-2">Order History</div>
                  {detail.orders.length === 0 && detail.pos_orders.length === 0 && <p className="text-stone-400 text-sm">No orders yet</p>}
                  {detail.orders.map((o) => (
                    <div key={o.order_id} className="flex justify-between text-sm border-b border-stone-100 py-2">
                      <span className="font-mono text-primary">{o.order_no}</span>
                      <span className="text-stone-600">{o.occasion || 'Custom'} · {o.status}</span>
                      <span className="font-semibold">{fmt(o.total_amount)}</span>
                    </div>
                  ))}
                  {detail.pos_orders.map((p) => (
                    <div key={p.order_id} className="flex justify-between text-sm border-b border-stone-100 py-2">
                      <span className="font-mono text-stone-500">{p.invoice_no}</span>
                      <span className="text-stone-600">POS Sale</span>
                      <span className="font-semibold">{fmt(p.grand_total)}</span>
                    </div>
                  ))}
                </div>
              </div>
            </>
          )}
        </DialogContent>
      </Dialog>
    </Layout>
  );
}
